import React, {useMemo, useState} from 'react';
import {scalableOutputTypes} from '@/views/storyboard/utils/chartConstants';
import {produce} from 'immer';
import useGlobalFiltersCombined from '@/views/storyboard/filters/useGlobalFiltersCombined';
import mergeLocalAndGlobalFilters from '@/views/storyboard/filters/mergeLocalAndGlobalFilters';
import {ChartAggregationCalcType} from '@/views/storyboard/utils/types';
import {addMovingAverageToData} from '@/views/storyboard/utils/calculations/chartMovingAverage';
import {addLinearRegressionToData} from '@/views/storyboard/utils/calculations/chartLinearRegression';
import {getScaledChartData} from '@/views/storyboard/utils/calculations/chartScaling';
import {getChartDataTotal} from '@/views/storyboard/utils/calculations/chartTotal';
import {ChartComponentProps} from '@/views/storyboard/charts/ChartContainer';
import {
    AggregationsChartData,
    MetricAggregationHistogramChartData,
    MetricAggregationSingleNumberChartData,
    MetricTimelineAggregatedChartData,
    MetricTimelineHistogramChartData,
    MultiTermsTimelineChartData,
    TermsTimelineChartData,
    TimelineByDateChartData
} from 'entities/chartData';
import {BarOrientation, BarSortOrder, ChartOutput, DataLabelPosition} from 'entities/chart';
import {notRenderedKeysArray, sortLegendKeys} from '../utils/SortLegendKeys';
import BarCharts from './BarChart';
import ChartIndicators from '../utils/ChartIndicators';
import {debounce} from 'lodash';
import {getFooterAggregationType} from '@/views/storyboard/charts/utils/chartFooterData';
import {isTimelineChartOutput} from '@/views/storyboard/utils/chartSettingsFieldVisibility';
import {sortBarChartData} from './sortBarChartData';

export type BarChartData = (
    TimelineByDateChartData
    | TermsTimelineChartData
    | MultiTermsTimelineChartData
    | MetricTimelineAggregatedChartData
    | MetricTimelineHistogramChartData
    | MetricAggregationSingleNumberChartData
    | MetricAggregationHistogramChartData
    | AggregationsChartData
    );

const BarChartWrapper = React.memo<ChartComponentProps>((
    {
        dataLooselyTyped,
        height,
        width,
        title,
        linearRegression,
        comparablePeriod,
        movingAverageDays = null,
        movingAverageDaysCenter = true,
        scalingFactor,
        barOrientation = BarOrientation.Vertical,
        barSortOrder = BarSortOrder.HighToLow,
        dataLabelPosition = DataLabelPosition.Off,
        chartQuery,
        fieldToQuery,
        searchViewCheck = false,
        queryDuration = '0',
        chartSettingsView,
        description = '',
        datePeriod = '',
        onPreviewFooterChange,
    },
) => {
    const data = dataLooselyTyped as BarChartData[];
    const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
    const debouncedSetHiddenKeys = useMemo(() => debounce(setHiddenKeys, 150), []);

    const isTermsOutput = chartQuery.output === ChartOutput.Terms;
    const shouldSortBarData = !isTimelineChartOutput(chartQuery.output);

    const keys = Object.keys(data[0] ?? {});

    let dataKeyLabel: string | number = keys.includes('dataLabel') ? 'dataLabel' : 'labelShort';
    if (keys.includes('singleNumber')) {
        dataKeyLabel = (data as MetricAggregationSingleNumberChartData[])?.[0]?.Result || 0;
    }

    const [distinctKeys] = useMemo<[string[]]>(
        () => {
            let filteredKeys: string[] = [];

            const getDistinctValues = (key: string): void => {
                if (!filteredKeys.concat(notRenderedKeysArray).includes(key)) {
                    filteredKeys.push(key);
                }
            };

            for (let i = 0; i < data.length; i++) {
                Object.keys(data[i]).forEach((key) => getDistinctValues(key));
            }

            filteredKeys = sortLegendKeys(filteredKeys);

            return [filteredKeys];
        }, [data]);

    let xAxisName: string | number;

    if (isTermsOutput) {
        xAxisName = data[0] && 'labelShort' in data[0] && (data[0] as TimelineByDateChartData).labelShort
            ? 'labelShort'
            : 'key';
    } else if (dataKeyLabel !== 'dataLabel') {
        xAxisName = dataKeyLabel;
    } else {
        xAxisName = 'tickValue';
    }

    const total = getChartDataTotal(data, distinctKeys, hiddenKeys);

    const distinctKeysComparable = ['comparableDocCount'];
    const totalComparable = getChartDataTotal(data, distinctKeysComparable, hiddenKeys);

    let trendValue;
    if (totalComparable > 0) {
        trendValue = (total / totalComparable * 100 - 100).toFixed(2);
    } else if (total > 0) {
        trendValue = (100).toFixed(2);
    } else {
        trendValue = (0).toFixed(2);
    }
    const footerAggregationType = getFooterAggregationType(chartQuery);
    const processedData = useMemo(() => {
        let result = [...data];

        if (chartQuery.output && scalableOutputTypes.includes(chartQuery.output)) {
            result = getScaledChartData(result, distinctKeys, scalingFactor);
        }

        if (!isTermsOutput && linearRegression) {
            result = addLinearRegressionToData(result, distinctKeys, ChartAggregationCalcType.Cumulative, hiddenKeys);
        }

        if (!isTermsOutput && movingAverageDays && movingAverageDays > 0) {
            result = addMovingAverageToData(
                result,
                distinctKeys,
                movingAverageDays,
                movingAverageDaysCenter,
                ChartAggregationCalcType.Cumulative,
                hiddenKeys
            );
        }

        if (shouldSortBarData) {
            result = sortBarChartData(result, distinctKeys, barSortOrder);
        }

        return result;
    }, [data, distinctKeys, chartQuery.output, scalingFactor, linearRegression, movingAverageDays, movingAverageDaysCenter, hiddenKeys, shouldSortBarData, barSortOrder]);

    const [globalAllFields, globalAdditionalFilters] = useGlobalFiltersCombined();
    const [finalAllFields, finalAdditionalFilters] = mergeLocalAndGlobalFilters(
        chartQuery.all_fields,
        chartQuery.additionalFilters,
        globalAllFields,
        globalAdditionalFilters,
    );

    const finalChartQuery = produce(chartQuery, (draft) => {
        draft.all_fields = finalAllFields;
        draft.additionalFilters = finalAdditionalFilters;
    });

    if (distinctKeys.includes('Other')) {
        if (distinctKeys[distinctKeys.length - 1] != 'Other') {
            const otherIndex = distinctKeys.indexOf('Other');
            distinctKeys.splice(otherIndex, 1);
            distinctKeys.push('Other');
        }
    }

    return (
        <>
            <BarCharts
                data={processedData}
                distinctKeysArray={distinctKeys}
                chartQuery={chartQuery}
                finalChartQuery={finalChartQuery}
                fieldToQuery={fieldToQuery}
                comparablePeriod={comparablePeriod}
                movingAverage={isTermsOutput ? null : movingAverageDays}
                linearRegression={isTermsOutput ? false : linearRegression}
                xAxisName={xAxisName}
                barOrientation={barOrientation}
                dataLabelPosition={dataLabelPosition}
                height={height}
                width={width}
                onLegendToggle={debouncedSetHiddenKeys}
                chartSettingsView={chartSettingsView}
                title={title}
                description={description}
                datePeriod={datePeriod}
            />

            {!searchViewCheck ? (
                <ChartIndicators
                    total={total}
                    totalComparable={totalComparable}
                    trendValue={trendValue}
                    queryDuration={queryDuration}
                    chartQueryOutput={chartQuery.output}
                    aggregationType={footerAggregationType}
                    chartSettingsView={chartSettingsView}
                    onPreviewFooterChange={onPreviewFooterChange}
                />
            ) : null
            }
        </>
    );
});

export default BarChartWrapper;
